import { useState } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import type { TileLayout } from '../hooks/useTilePersistence';
import _ from 'lodash';

type ChartType = 'line' | 'bar' | 'single';

const chartTypeTitlePrefix: Record<ChartType, string> = {
  line: 'line',
  bar: 'bar',
  single: 'Latest value',
};

export interface TileEditorProps {
  show: boolean;
  onClose: () => void;
  onAdd: (tile: Omit<TileLayout, 'id'>) => void;
  isLoading?: boolean;
}

/**
 * Modal component for creating a new dashboard tile.
 * Collects title, datasource ID, and chart type from the user.
 */
export function TileEditor({ show, onClose, onAdd, isLoading = false }: TileEditorProps) {
  const [newTile, setNewTile] = useState<{
    title: string;
    dataSource: string;
    chartType: ChartType;
  }>({
    title: '',
    dataSource: '',
    chartType: 'line',
  });

  const handleAdd = async () => {
    if (!newTile.dataSource || !newTile.chartType) return;

    const title = newTile.title || _.capitalize(
      `${chartTypeTitlePrefix[newTile.chartType]} chart for ${newTile.dataSource}`
    );

    onAdd({ ...newTile, title });
    setNewTile({ title: '', dataSource: '', chartType: 'line' });
  };

  const handleInputChange = (name: string, value: string) => {
    setNewTile((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <Modal show={show} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title>Add a new dashboard tile</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Tile Title (Optional)</Form.Label>
            <Form.Control
              type="text"
              name="title"
              value={newTile.title}
              onChange={(e) => setNewTile((prev) => ({ ...prev, title: e.target.value }))}
              placeholder="Enter a title for this tile"
              disabled={isLoading}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Data Source ID*</Form.Label>
            <Form.Select
              name="dataSource"
              value={newTile.dataSource}
              onChange={(e) => handleInputChange(e.target.name, e.target.value)}
              disabled={isLoading}
              required
            >
              <option value="">Select a data source</option>
              <option key="todos" value="todos">
                Number of Todos
              </option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Chart Type*</Form.Label>
            <div>
              <Form.Check
                inline
                type="radio"
                id="chart-line"
                label="Line Chart"
                name="chartType"
                value="line"
                checked={newTile.chartType === 'line'}
                onChange={(e) => handleInputChange(e.target.name, e.target.value)}
                disabled={isLoading}
              />
              <Form.Check
                inline
                type="radio"
                id="chart-bar"
                label="Bar Chart"
                name="chartType"
                value="bar"
                checked={newTile.chartType === 'bar'}
                onChange={(e) => handleInputChange(e.target.name, e.target.value)}
                disabled={isLoading}
              />
              <Form.Check
                inline
                type="radio"
                id="chart-single"
                label="Single Value"
                name="chartType"
                value="single"
                checked={newTile.chartType === 'single'}
                onChange={(e) => handleInputChange(e.target.name, e.target.value)}
                disabled={isLoading}
              />
            </div>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleAdd}
          disabled={isLoading || !newTile.dataSource || !newTile.chartType}
        >
          {isLoading ? (
            <>
              <Spinner as="span" animation="border" size="sm" role="status" /> Fetching Data...
            </>
          ) : (
            'Add Tile'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
